/*
 * Stair Lights Configurator — controller
 * One-off page widget at /pages/stair-lights-configurator.
 * Vanilla IIFE, no framework. Reads server-rendered data from #slc-data
 * (prices are already B2B-catalog-correct from Liquid). No Storefront API call.
 */
(function () {
  'use strict';

  /* ── Constants ── */
  var MIN_STEPS = 4;
  var CAP_500 = 64;
  var CAP_1000 = 40;
  var HEADROOM = 1.15;
  var PHONE = '0333 443 2465';
  var EMAIL = 'sales@simplelighting.co.uk';
  var STORAGE_KEY = 'sl_stair_configurator_state';

  /* ── Data ── */
  var root = document.getElementById('slc-root');
  var dataEl = document.getElementById('slc-data');
  if (!root || !dataEl) return;

  var data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  if (!data || !data.components || !Array.isArray(data.drivers)) return;

  var components = data.components;
  var meta = data.meta || {};

  /* ── State ── */
  var state = {
    step: 1,
    count: 17,
    barLength: null /* 500 | 1000 */
  };
  /* Derived (not persisted) */
  var driver = null;
  var driverError = null; /* null | 'oos' | 'over-range' */

  /* ── DOM refs ── */
  var refs = {
    error: document.getElementById('slc-error'),
    announce: document.getElementById('slc-announce'),
    progress: document.getElementById('slc-progress'),
    steps: Array.prototype.slice.call(root.querySelectorAll('.slc__step')),
    count: document.getElementById('slc-count'),
    rangeHint: document.getElementById('slc-range-hint'),
    stairs: document.getElementById('slc-stairs'),
    barRadios: Array.prototype.slice.call(root.querySelectorAll('input[name="slc-bar"]')),
    explainerToggle: document.getElementById('slc-explainer-toggle'),
    explainerBody: document.getElementById('slc-explainer-body'),
    driverResult: document.getElementById('slc-driver-result'),
    reviewList: document.getElementById('slc-review-list'),
    topCtaSummary: document.getElementById('slc-top-cta-summary'),
    /* summary panel */
    sumSubtitle: document.getElementById('slc-summary-subtitle'),
    sumSteps: document.getElementById('slc-sum-steps'),
    sumBar: document.getElementById('slc-sum-bar'),
    sumDriver: document.getElementById('slc-sum-driver'),
    sumBarsLabel: document.getElementById('slc-sum-bars-label'),
    sumBarsPrice: document.getElementById('slc-sum-bars-price'),
    sumTconnLabel: document.getElementById('slc-sum-tconn-label'),
    sumTconnPrice: document.getElementById('slc-sum-tconn-price'),
    sumControllerPrice: document.getElementById('slc-sum-controller-price'),
    sumDriverLabel: document.getElementById('slc-sum-driver-label'),
    sumDriverPrice: document.getElementById('slc-sum-driver-price'),
    sumTotal: document.getElementById('slc-sum-total'),
    sumSaving: document.getElementById('slc-sum-saving'),
    summaryCta: document.getElementById('slc-summary-cta'),
    /* sticky bar */
    stickyLabel: document.getElementById('slc-sticky-label'),
    stickyTotal: document.getElementById('slc-sticky-total'),
    stickyNext: document.getElementById('slc-sticky-next'),
    stickyAdd: document.getElementById('slc-sticky-add'),
    addLabels: Array.prototype.slice.call(root.querySelectorAll('[data-slc-add-label]'))
  };

  /* ── VAT-aware price formatting ──
     Liquid renders prices in pence, tax-inclusive (UK). The customer's stored
     preference (set ex by default for B2B in the layout) drives display. */
  function getVatMode() {
    try {
      return localStorage.getItem('vatPreference') || (root.getAttribute('data-vat-default') || 'inc');
    } catch (e) {
      return root.getAttribute('data-vat-default') || 'inc';
    }
  }
  function formatPrice(pence) {
    if (typeof pence !== 'number' || isNaN(pence)) return '£—';
    var exVat = getVatMode() === 'ex';
    var display = exVat ? Math.round(pence / 1.2) : pence;
    return '£' + (display / 100).toFixed(2);
  }

  /* ── Derived helpers ── */
  function currentBar() {
    if (state.barLength === 500) return components.bar500;
    if (state.barLength === 1000) return components.bar1000;
    return null;
  }
  function barWatts() {
    var b = currentBar();
    return b ? b.watts : 0;
  }
  function currentCap() {
    return state.barLength === 1000 ? CAP_1000 : CAP_500;
  }
  function tConnectorQty() {
    return state.count + 2;
  }
  function totalLoad() {
    return state.count * barWatts();
  }

  /* ── Driver selection ──
     is_driver true, non-dimmable, voltage list includes 24, max_watts sufficient
     with 15% headroom. In-stock preferred; smallest sufficient wins, cheapest on
     a wattage tie. If sufficient drivers exist but all are OOS -> 'oos'. If no
     driver can carry the load at all -> 'over-range'. */
  function selectDriver() {
    if (!state.barLength) {
      driver = null;
      driverError = null;
      return;
    }
    var required = totalLoad() * HEADROOM;

    var candidates = data.drivers.filter(function (d) {
      return (
        d.isDriver === true &&
        d.isDimmable === false &&
        Array.isArray(d.voltage) &&
        d.voltage.some(function (v) {
          return Number(v) === 24;
        }) &&
        typeof d.maxWatts === 'number' &&
        d.maxWatts > 0
      );
    });

    var sufficient = candidates.filter(function (d) {
      return d.maxWatts >= required;
    });
    sufficient.sort(function (a, b) {
      return a.maxWatts - b.maxWatts || a.price - b.price;
    });

    var inStock = sufficient.filter(function (d) {
      return d.available === true;
    });

    if (inStock.length) {
      driver = inStock[0];
      driverError = null;
    } else if (sufficient.length) {
      /* sufficient drivers exist but every one is out of stock */
      driver = null;
      driverError = 'oos';
    } else {
      /* load exceeds even the largest non-dimmable 24V driver */
      driver = null;
      driverError = 'over-range';
    }
  }

  /* ── Totals ── */
  function lineUnits() {
    /* Returns the 4 components with quantities, or null if not resolvable. */
    var bar = currentBar();
    if (!bar || !driver) return null;
    return [
      { key: 'bar', comp: bar, quantity: state.count, role: 'led-bar' },
      { key: 'tconnector', comp: components.tconnector, quantity: tConnectorQty(), role: 't-connector' },
      { key: 'controller', comp: components.controller, quantity: 1, role: 'controller' },
      { key: 'driver', comp: driver, quantity: 1, role: 'driver' }
    ];
  }
  function kitTotal() {
    var units = lineUnits();
    if (!units) return null;
    return units.reduce(function (sum, u) {
      return sum + u.comp.price * u.quantity;
    }, 0);
  }
  function kitSaving() {
    /* Sum (compareAt - price) * qty across components. compareAt missing -> 0. */
    var units = lineUnits();
    if (!units) return 0;
    return units.reduce(function (sum, u) {
      var cmp = typeof u.comp.compareAt === 'number' ? u.comp.compareAt : u.comp.price;
      var diff = (cmp - u.comp.price) * u.quantity;
      return sum + (diff > 0 ? diff : 0);
    }, 0);
  }

  /* ── Persistence ── */
  function persist() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step: state.step, count: state.count, barLength: state.barLength })
      );
    } catch (e) {
      /* sessionStorage unavailable — non-fatal */
    }
  }
  function restore() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (!saved) return;
      if (typeof saved.count === 'number') state.count = saved.count;
      if (saved.barLength === 500 || saved.barLength === 1000) state.barLength = saved.barLength;
      if (typeof saved.step === 'number' && saved.step >= 1 && saved.step <= 4) state.step = saved.step;
    } catch (e) {
      /* ignore corrupt state */
    }
  }
  function clearPersisted() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* non-fatal */
    }
  }

  /* ── Announce / error ── */
  function announce(msg) {
    if (refs.announce) refs.announce.textContent = msg;
  }
  function showError(msg) {
    if (!refs.error) return;
    refs.error.textContent = msg;
    refs.error.hidden = false;
  }
  function clearError() {
    if (!refs.error) return;
    refs.error.textContent = '';
    refs.error.hidden = true;
  }

  /* ── Count clamping ── */
  function clampCount(value) {
    var n = parseInt(value, 10);
    if (isNaN(n)) n = MIN_STEPS;
    var cap = currentCap();
    if (n < MIN_STEPS) n = MIN_STEPS;
    if (n > cap) n = cap;
    return n;
  }
  function setCount(value, opts) {
    var clamped = clampCount(value);
    var changed = clamped !== state.count;
    state.count = clamped;
    selectDriver();
    if (opts && opts.announce && changed) {
      announce(state.count + ' stairs');
    }
    render();
    persist();
  }

  /* ── Step navigation ── */
  function canAdvanceFrom(step) {
    if (step === 1) return true; /* count is always clamped-valid */
    if (step === 2) return state.barLength === 500 || state.barLength === 1000;
    if (step === 3) return !!driver;
    return false;
  }
  function goToStep(target) {
    if (target < 1 || target > 4) return;
    if (target > state.step && !canAdvanceFrom(state.step)) return;

    /* Re-apply the bar-specific cap on advance (belt and braces). */
    if (state.barLength === 1000 && state.count > CAP_1000) {
      state.count = CAP_1000;
    }
    selectDriver();
    clearError();
    state.step = target;
    render();
    persist();

    /* Focus the step heading for screen-reader continuity. */
    var panel = root.querySelector('.slc__step[data-step="' + target + '"]');
    var heading = panel && panel.querySelector('.slc__step-title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
    announce('Step ' + target + ' of 4');
    /* Scroll back to the top of the configurator on step change, clearing the
       theme's sticky header (var set on <body> by theme.liquid). */
    var headerOffset =
      parseInt(getComputedStyle(document.body).getPropertyValue('--header-group-height'), 10) || 0;
    var top = root.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
  }

  /* ── Rendering ── */
  function renderProgress() {
    var pills = refs.progress.querySelectorAll('.slc__pill');
    pills.forEach(function (pill) {
      var s = parseInt(pill.getAttribute('data-step'), 10);
      pill.classList.remove('slc__pill--active', 'slc__pill--done');
      pill.removeAttribute('aria-current');
      if (s < state.step) {
        pill.classList.add('slc__pill--done');
      } else if (s === state.step) {
        pill.classList.add('slc__pill--active');
        pill.setAttribute('aria-current', 'step');
      }
    });
  }

  function renderStepVisibility() {
    refs.steps.forEach(function (panel) {
      var s = parseInt(panel.getAttribute('data-step'), 10);
      panel.hidden = s !== state.step;
    });
  }

  function renderStairs() {
    if (!refs.stairs) return;
    var cap = 28; /* visual cap — illustration stays legible for tall staircases */
    var bars = Math.min(state.count, cap);
    var html = '';
    for (var i = 0; i < bars; i++) {
      var width = 28 + (i / Math.max(bars - 1, 1)) * 66; /* 28% -> 94% */
      html += '<div class="slc__stair-step" style="width:' + width.toFixed(1) + '%"></div>';
    }
    refs.stairs.innerHTML = html;
  }

  function renderStep1() {
    if (refs.count) {
      refs.count.value = state.count;
      refs.count.min = MIN_STEPS;
      refs.count.max = currentCap();
    }
    if (refs.rangeHint) {
      refs.rangeHint.textContent = 'Range: ' + MIN_STEPS + '–' + currentCap() + ' steps';
    }
    var dec = root.querySelector('.slc__step[data-step="1"] [data-slc-action="decrement"]');
    var inc = root.querySelector('.slc__step[data-step="1"] [data-slc-action="increment"]');
    if (dec) dec.disabled = state.count <= MIN_STEPS;
    if (inc) inc.disabled = state.count >= currentCap();
    renderStairs();
  }

  function renderStep2() {
    refs.barRadios.forEach(function (radio) {
      var checked = parseInt(radio.value, 10) === state.barLength;
      radio.checked = checked;
      var card = radio.closest('.slc__bar-card');
      if (card) card.classList.toggle('slc__bar-card--selected', checked);
    });
  }

  function renderDriverResult() {
    if (!refs.driverResult) return;

    if (driverError === 'oos') {
      refs.driverResult.innerHTML =
        '<div class="slc__driver-fallback">' +
        '<p><strong>We are out of stock on the driver this kit needs.</strong></p>' +
        '<p>Please call <a href="tel:+443334432465">' + PHONE + '</a> or email ' +
        '<a href="mailto:' + EMAIL + '">' + EMAIL + '</a> and we will sort it for you.</p>' +
        '</div>';
      return;
    }
    if (driverError === 'over-range') {
      refs.driverResult.innerHTML =
        '<div class="slc__driver-fallback">' +
        '<p><strong>This configuration exceeds our standard driver range.</strong></p>' +
        '<p>Please call <a href="tel:+443334432465">' + PHONE + '</a> or email ' +
        '<a href="mailto:' + EMAIL + '">' + EMAIL + '</a> and we will build it for you.</p>' +
        '</div>';
      return;
    }
    if (!driver) {
      refs.driverResult.innerHTML =
        '<div class="slc__driver-fallback"><p>Choose a bar length to see your recommended driver.</p></div>';
      return;
    }

    var load = totalLoad();
    var required = load * HEADROOM;
    var headroomPct = Math.round(((driver.maxWatts - load) / load) * 100);
    var imgHtml = driver.image
      ? '<img class="slc__driver-thumb" src="' + escapeAttr(driver.image) + '" alt="" width="72" height="72" loading="lazy">'
      : '<div class="slc__driver-thumb slc__driver-thumb--empty" aria-hidden="true"></div>';

    refs.driverResult.innerHTML =
      '<div class="slc__driver-rec">' +
      '<div class="slc__driver-rec-header">' +
      '<span class="slc__driver-rec-icon" aria-hidden="true">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
      '</span>' +
      '<span class="slc__driver-rec-title">Recommended driver</span>' +
      '</div>' +
      '<div class="slc__driver-product">' +
      imgHtml +
      '<div class="slc__driver-info">' +
      '<h3 class="slc__driver-name">' + escapeHtml(driver.title) + '</h3>' +
      '<p class="slc__driver-meta">' + driver.maxWatts + 'W &middot; 24V &middot; non-dimmable' +
      (typeof driver.ipRating === 'number' ? ' &middot; IP' + driver.ipRating : '') + '</p>' +
      '</div>' +
      '<div class="slc__driver-price">' + formatPrice(driver.price) + '</div>' +
      '</div>' +
      '<div class="slc__driver-calc">' +
      '<strong>How we worked this out:</strong><br>' +
      state.count + ' bars &times; ' + barWatts() + 'W = <strong>' + load + 'W total load</strong> &middot; ' +
      '15% headroom = <strong>' + required.toFixed(1) + 'W minimum</strong> &middot; ' +
      'selected: <strong>' + driver.maxWatts + 'W driver</strong>' +
      (headroomPct > 0 ? ' (' + headroomPct + '% headroom).' : '.') +
      '</div>' +
      '</div>';
  }

  function renderReview() {
    if (!refs.reviewList) return;
    var units = lineUnits();
    if (!units) {
      refs.reviewList.innerHTML = '';
      return;
    }
    var html = '';
    units.forEach(function (u) {
      var note = '';
      if (u.role === 't-connector') {
        note = ' <span class="slc__review-note">(' + state.count + ' for stairs + 2 for PIR sensors)</span>';
      }
      var imgHtml = u.comp.image
        ? '<img class="slc__review-thumb" src="' + escapeAttr(u.comp.image) + '" alt="" width="48" height="48" loading="lazy">'
        : '<div class="slc__review-thumb slc__review-thumb--empty" aria-hidden="true"></div>';
      html +=
        '<li class="slc__review-item">' +
        imgHtml +
        '<div class="slc__review-detail">' +
        '<p class="slc__review-name">' + escapeHtml(u.comp.title) + note + '</p>' +
        '<span class="slc__review-sku">' + escapeHtml(u.comp.sku || '') + '</span>' +
        '</div>' +
        '<span class="slc__review-qty">&times; ' + u.quantity + '</span>' +
        '<span class="slc__review-price">' + formatPrice(u.comp.price * u.quantity) + '</span>' +
        '</li>';
    });
    refs.reviewList.innerHTML = html;
  }

  function kitLabel() {
    var bar = state.barLength ? state.barLength + 'mm' : '';
    return 'Custom Stair Kit · ' + state.count + ' step' + (bar ? ' · ' + bar : '');
  }

  function renderSummary() {
    var bar = currentBar();
    var total = kitTotal();
    var saving = kitSaving();

    /* progressive disclosure labels */
    if (refs.sumSteps) refs.sumSteps.textContent = state.count + ' stairs';
    if (refs.sumBar) {
      refs.sumBar.textContent = state.barLength ? state.barLength + 'mm' : 'Choose next';
    }
    if (refs.sumDriver) {
      refs.sumDriver.textContent = driver
        ? driver.maxWatts + 'W non-dimmable'
        : driverError
        ? 'See step 3'
        : 'Auto-selected';
    }

    if (refs.sumBarsLabel) refs.sumBarsLabel.textContent = 'LED bars × ' + state.count;
    if (refs.sumBarsPrice) {
      refs.sumBarsPrice.textContent = bar ? formatPrice(bar.price * state.count) : '—';
    }
    if (refs.sumTconnLabel) refs.sumTconnLabel.textContent = 'T-connectors × ' + tConnectorQty();
    if (refs.sumTconnPrice) {
      refs.sumTconnPrice.textContent = formatPrice(components.tconnector.price * tConnectorQty());
    }
    if (refs.sumControllerPrice) {
      refs.sumControllerPrice.textContent = formatPrice(components.controller.price);
    }
    if (refs.sumDriverLabel) {
      refs.sumDriverLabel.textContent = driver ? driver.maxWatts + 'W 24V driver' : '24V driver';
    }
    if (refs.sumDriverPrice) {
      refs.sumDriverPrice.textContent = driver ? formatPrice(driver.price) : '—';
    }

    if (refs.sumTotal) {
      refs.sumTotal.textContent = total != null ? formatPrice(total) : '£—';
    }
    if (refs.sumSaving) {
      if (saving > 0) {
        refs.sumSaving.textContent = formatPrice(saving) + ' off vs buying separately';
        refs.sumSaving.hidden = false;
      } else {
        refs.sumSaving.hidden = true;
        refs.sumSaving.textContent = '';
      }
    }
    if (refs.sumSubtitle) {
      refs.sumSubtitle.textContent =
        state.step === 4 ? kitLabel().replace('Custom Stair Kit · ', '') : 'Updates as you choose';
    }

    /* summary-panel CTA only on the review step */
    if (refs.summaryCta) refs.summaryCta.hidden = state.step !== 4;

    /* sticky bar (mobile) */
    if (refs.stickyLabel) {
      refs.stickyLabel.textContent = state.barLength
        ? state.count + '-step · ' + state.barLength + 'mm' + (driver ? ' · ' + driver.maxWatts + 'W' : '')
        : state.count + '-step · pending choices';
    }
    if (refs.stickyTotal) {
      refs.stickyTotal.textContent = total != null ? formatPrice(total) : '£—';
    }

    /* top-cta one-line summary */
    if (refs.topCtaSummary) {
      if (total != null) {
        refs.topCtaSummary.textContent =
          state.count + '-step kit · ' + state.barLength + 'mm bars · ' +
          driver.maxWatts + 'W driver · ' + formatPrice(total) + ' total';
      } else {
        refs.topCtaSummary.textContent = 'Your custom stair light kit';
      }
    }

    /* add-to-basket button labels with price */
    var addLabel = total != null ? 'Add custom kit to basket · ' + formatPrice(total) : 'Add custom kit to basket';
    refs.addLabels.forEach(function (el) {
      el.textContent = addLabel;
    });
  }

  function renderNav() {
    /* in-panel back/next buttons */
    refs.steps.forEach(function (panel) {
      var s = parseInt(panel.getAttribute('data-step'), 10);
      var next = panel.querySelector('[data-slc-action="next"]');
      if (next) next.disabled = !canAdvanceFrom(s);
    });
    /* sticky bar */
    var stickyBackBtn = document.querySelector('.slc__sticky-bar [data-slc-action="back"]');
    if (stickyBackBtn) stickyBackBtn.hidden = state.step === 1;
    if (refs.stickyNext) {
      refs.stickyNext.hidden = state.step === 4;
      refs.stickyNext.disabled = !canAdvanceFrom(state.step);
      refs.stickyNext.textContent =
        state.step === 1 ? 'Continue' : state.step === 3 ? 'Review' : 'Continue';
    }
    if (refs.stickyAdd) refs.stickyAdd.hidden = state.step !== 4;
  }

  function render() {
    renderProgress();
    renderStepVisibility();
    renderStep1();
    renderStep2();
    renderDriverResult();
    renderReview();
    renderSummary();
    renderNav();
  }

  /* ── Add to cart ── */
  function buildCartPayload() {
    var units = lineUnits();
    if (!units) return null;
    var bundleId =
      'sl-' +
      (window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID().slice(0, 8)
        : Math.random().toString(36).slice(2, 10));
    var label = kitLabel();
    return {
      bundleId: bundleId,
      items: units.map(function (u) {
        return {
          id: u.comp.variantId,
          quantity: u.quantity,
          properties: {
            _bundle_id: bundleId,
            _bundle_label: label,
            _bundle_parent_handle: meta.parentHandle || 'custom-stair-kit',
            _bundle_role: u.role
          }
        };
      })
    };
  }

  /* Validation rule #4: re-confirm every component + driver is available right
     now (the page may have been open a while). Hits /products/{handle}.js. */
  function verifyAvailability() {
    var units = lineUnits();
    if (!units) return Promise.resolve(false);
    var checks = units.map(function (u) {
      var handle = u.comp.handle;
      if (!handle) return Promise.resolve(true); /* can't check — trust page-load data */
      return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (product) {
          if (!product || !Array.isArray(product.variants)) return true;
          var variant = product.variants.filter(function (v) {
            return v.id === u.comp.variantId;
          })[0];
          return variant ? variant.available === true : false;
        })
        .catch(function () {
          return true; /* network hiccup — don't block on it */
        });
    });
    return Promise.all(checks).then(function (results) {
      return results.every(Boolean);
    });
  }

  function fireTrackingEvent(total, units, bundleId) {
    /* Decision #9: ONE add_to_cart event per bundle (not per line item). */
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'GBP',
          value: total / 100,
          items: units.map(function (u) {
            return {
              item_id: u.comp.sku || String(u.comp.variantId),
              item_name: u.comp.title,
              price: u.comp.price / 100,
              quantity: u.quantity
            };
          })
        },
        parent_sku: meta.parentSku || 'SL-STAIR-KIT-CUSTOM',
        bundle_id: bundleId
      });
    } catch (e) {
      /* tracking must never block the add-to-cart */
    }
  }

  var isAdding = false;
  function addToCart() {
    if (isAdding) return;
    clearError();

    /* Validation gate */
    if (!(state.barLength === 500 || state.barLength === 1000)) {
      showError('Please choose a bar length before adding your kit.');
      goToStep(2);
      return;
    }
    if (!driver) {
      showError(
        driverError === 'over-range'
          ? 'This configuration exceeds our standard driver range. Please call ' + PHONE + '.'
          : 'We could not select a driver for this kit. Please call ' + PHONE + '.'
      );
      goToStep(3);
      return;
    }
    var payload = buildCartPayload();
    var total = kitTotal();
    if (!payload || total == null) {
      showError('Something went wrong building your kit. Please refresh and try again.');
      return;
    }

    isAdding = true;
    setAddButtonsBusy(true);

    verifyAvailability()
      .then(function (allAvailable) {
        if (!allAvailable) {
          throw new Error(
            'One or more items in your kit have just gone out of stock. Please call ' +
              PHONE +
              ' and we will help.'
          );
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload.items })
        });
      })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.body && result.body.status)) {
          var msg =
            (result.body && (result.body.description || result.body.message)) ||
            'We could not add your kit to the basket. Please try again.';
          throw new Error(msg);
        }
        /* success */
        fireTrackingEvent(total, lineUnits(), payload.bundleId);
        clearPersisted();
        window.location.href = '/cart';
      })
      .catch(function (err) {
        isAdding = false;
        setAddButtonsBusy(false);
        showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
      });
  }

  function setAddButtonsBusy(busy) {
    var addButtons = root.querySelectorAll('[data-slc-action="add"]');
    addButtons.forEach(function (btn) {
      btn.disabled = busy;
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
    if (busy) announce('Adding your kit to the basket');
  }

  /* ── Utilities ── */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }
  function escapeAttr(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ── Events ── */
  function bindEvents() {
    /* Delegated action buttons (next / back / add / increment / decrement) */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-slc-action]');
      if (!btn || btn.disabled) return;
      var action = btn.getAttribute('data-slc-action');
      if (action === 'next') {
        goToStep(state.step + 1);
      } else if (action === 'back') {
        goToStep(state.step - 1);
      } else if (action === 'add') {
        addToCart();
      } else if (action === 'increment') {
        setCount(state.count + 1, { announce: true });
      } else if (action === 'decrement') {
        setCount(state.count - 1, { announce: true });
      }
    });

    /* Count input */
    if (refs.count) {
      refs.count.addEventListener('input', function () {
        /* allow free typing; clamp on change/blur */
      });
      refs.count.addEventListener('change', function () {
        setCount(refs.count.value);
      });
      refs.count.addEventListener('blur', function () {
        setCount(refs.count.value);
      });
      refs.count.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          setCount(refs.count.value);
        }
      });
    }

    /* Bar length radios */
    refs.barRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        state.barLength = parseInt(radio.value, 10);
        /* enforce the 1000mm cap immediately */
        if (state.barLength === 1000 && state.count > CAP_1000) {
          state.count = CAP_1000;
          announce('Step count adjusted to ' + CAP_1000 + ', the maximum for 1000mm bars.');
        }
        selectDriver();
        clearError();
        render();
        persist();
      });
    });

    /* Explainer toggle */
    if (refs.explainerToggle && refs.explainerBody) {
      refs.explainerToggle.addEventListener('click', function () {
        var expanded = refs.explainerToggle.getAttribute('aria-expanded') === 'true';
        refs.explainerToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        refs.explainerBody.hidden = expanded;
      });
    }
  }

  /* Hide the mobile sticky bar once the site footer scrolls into view, so it
     never overlaps the footer. The bar is position:fixed; this is the same
     approach the theme's own sticky-add-to-cart uses. */
  function watchFooterForStickyBar() {
    var stickyBar = document.getElementById('slc-sticky-bar');
    var footer = document.querySelector('footer');
    if (!stickyBar || !footer || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        stickyBar.classList.toggle('slc__sticky-bar--hidden', entry.isIntersecting);
      });
    });
    observer.observe(footer);
  }

  /* ── Init ── */
  function init() {
    restore();
    /* Re-derive: clamp restored count to whatever cap the restored bar implies. */
    state.count = clampCount(state.count);
    selectDriver();
    /* If a stale restored step is no longer reachable, walk it back. */
    while (state.step > 1 && !canAdvanceFrom(state.step - 1)) {
      state.step--;
    }
    bindEvents();
    watchFooterForStickyBar();
    render();
  }

  init();
})();
